/**
 * ScanPage
 * Kitchly v0.5.3
 *
 * Uses html5-qrcode for camera barcode scanning.
 * Supports EAN-13, EAN-8, UPC-A, UPC-E, CODE-128, CODE-39.
 *
 * v0.5.3: Quantity selector (plus/minus), improved iPhone camera config,
 *         fallback barcode API support, manual product entry for unknown barcodes.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Camera,
  X,
  Search,
  Plus,
  Minus,
  PlusCircle,
  ArrowLeft,
  MapPin,
  Package
} from 'lucide-react';
import { inventory, locations as locationsApi } from '../services/api';

function ScanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scannerRef = useRef(null);

  const [mode, setMode] = useState('idle'); // idle, scanning, manual, result, duplicate, notfound
  const [manualBarcode, setManualBarcode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(false);

  useEffect(() => {
    locationsApi.list()
      .then(data => setLocations(data.locations || []))
      .catch(() => {});

    // Cleanup scanner on unmount
    return () => stopScanner();
  }, []);

  const stopScanner = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.stop().then(() => {
        scannerRef.current.clear();
        scannerRef.current = null;
      }).catch(() => {
        try { scannerRef.current.clear(); } catch (e) {}
        scannerRef.current = null;
      });
    }
  }, []);

  const startCamera = async () => {
    setMode('scanning');
    setCameraError(false);

    // Small delay to let the DOM render the scanner container
    await new Promise(r => setTimeout(r, 100));

    try {
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import('html5-qrcode');

      const scanner = new Html5Qrcode('kitchly-scanner', {
        formatsToSupport: [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.UPC_EAN_EXTENSION,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39
        ],
        verbose: false
      });

      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 15,
          qrbox: { width: 300, height: 150 },
          aspectRatio: 1.777,
          disableFlip: false,
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true
          }
        },
        (decodedText) => {
          // Barcode detected — stop scanning and look up
          scanner.stop().then(() => {
            scanner.clear();
            scannerRef.current = null;
            handleBarcodeLookup(decodedText);
          }).catch(() => {
            scannerRef.current = null;
            handleBarcodeLookup(decodedText);
          });
        },
        () => {
          // Scan error — ignore, keep scanning
        }
      );
    } catch (err) {
      console.error('[Scan] Camera error:', err);
      setCameraError(true);
      setMode('manual');
      toast.error(t('scan.cameraError'));
    }
  };

  const handleBarcodeLookup = async (barcode) => {
    setLoading(true);
    setQuantity(1); // Reset quantity for new scan
    try {
      const result = await inventory.scan(barcode);
      setScanResult(result);

      if (result.duplicate) {
        setMode('duplicate');
      } else if (result.found) {
        setMode('result');
        if (result.suggested_location) {
          setSelectedLocation(String(result.suggested_location.id));
        }
      } else {
        // Product not found in any API — offer manual entry
        setMode('notfound');
      }
    } catch {
      toast.error(t('common.error'));
      setMode('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualBarcode.trim()) return;
    stopScanner();
    handleBarcodeLookup(manualBarcode.trim());
  };

  const handleAddToInventory = async () => {
    if (!scanResult?.product) return;
    setLoading(true);

    try {
      await inventory.confirmScan({
        product_id: scanResult.product.id,
        action: 'new',
        quantity: quantity,
        unit: 'piece',
        location_id: selectedLocation ? parseInt(selectedLocation) : undefined
      });

      toast.success(t('scan.added', { name: scanResult.product.name }));
      resetScan();
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateAction = async (action, existingItemId) => {
    if (!scanResult?.product) return;
    setLoading(true);

    try {
      await inventory.confirmScan({
        product_id: scanResult.product.id,
        action,
        existing_item_id: existingItemId,
        quantity: quantity
      });

      const actionLabel = action === 'increment' ? t('scan.increment') : t('scan.addNew');
      toast.success(`${actionLabel}: ${scanResult.product.name}`);
      resetScan();
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const incrementQty = () => setQuantity(prev => prev + 1);
  const decrementQty = () => setQuantity(prev => (prev > 1 ? prev - 1 : 1));

  const resetScan = () => {
    stopScanner();
    setScanResult(null);
    setSelectedLocation('');
    setManualBarcode('');
    setQuantity(1);
    setMode('idle');
  };

  // Reusable quantity selector component
  const QuantitySelector = () => (
    <div className="form-group">
      <label className="form-label">{t('addItem.quantity')}</label>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        justifyContent: 'center'
      }}>
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          onClick={decrementQty}
          disabled={quantity <= 1}
          style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-full)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
          }}
        >
          <Minus size={20} />
        </button>
        <span style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          minWidth: 48,
          textAlign: 'center',
          fontFamily: 'var(--font-heading)'
        }}>
          {quantity}
        </span>
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          onClick={incrementQty}
          style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-full)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0
          }}
        >
          <Plus size={20} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="scan-page" style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: 'var(--space-md)', gap: 'var(--space-md)' }}>
        <button className="btn-icon" onClick={() => { stopScanner(); navigate(-1); }}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ flex: 1, margin: 0 }}>{t('scan.title')}</h1>
      </div>

      <div style={{ padding: '0 var(--space-md)' }}>

        {/* === IDLE STATE === */}
        {mode === 'idle' && (
          <div className="empty-state">
            <Camera size={64} className="empty-state-icon" />
            <h3>{t('scan.title')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', marginTop: 'var(--space-lg)' }}>
              <button className="btn btn-primary btn-lg btn-full" onClick={startCamera}>
                <Camera size={20} /> {t('scan.scanning')}
              </button>
              <button className="btn btn-secondary btn-lg btn-full" onClick={() => setMode('manual')}>
                <Search size={20} /> {t('scan.manualEntry')}
              </button>
            </div>
          </div>
        )}

        {/* === SCANNING STATE === */}
        {mode === 'scanning' && (
          <div>
            <div
              id="kitchly-scanner"
              style={{
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                marginBottom: 'var(--space-md)',
                width: '100%'
              }}
            />

            <p className="text-sm text-center text-secondary mb-md">
              {t('scan.holdSteady')}
            </p>

            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button className="btn btn-secondary btn-full" onClick={() => { stopScanner(); setMode('manual'); }}>
                {t('scan.manualEntry')}
              </button>
              <button className="btn btn-ghost btn-full" onClick={() => { stopScanner(); setMode('idle'); }}>
                {t('scan.stopCamera')}
              </button>
            </div>
          </div>
        )}

        {/* === MANUAL ENTRY STATE === */}
        {mode === 'manual' && (
          <div className="card">
            <h3 className="card-title mb-md">{t('scan.manualEntry')}</h3>
            {cameraError && (
              <div className="banner banner-info mb-md">
                {t('scan.cameraError')}
              </div>
            )}
            <form onSubmit={handleManualSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  className="form-input"
                  placeholder={t('scan.manualPlaceholder')}
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  autoFocus
                  inputMode="numeric"
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button type="submit" className="btn btn-primary btn-full" disabled={loading || !manualBarcode.trim()}>
                  {loading ? t('common.loading') : t('scan.lookup')}
                </button>
                <button type="button" className="btn btn-ghost" onClick={resetScan}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* === RESULT STATE (product found) === */}
        {mode === 'result' && scanResult && scanResult.found && (
          <div className="card">
            <div className="flex items-center gap-md mb-md">
              {scanResult.product.image_url ? (
                <img
                  src={scanResult.product.image_url}
                  alt={scanResult.product.name}
                  style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                />
              ) : (
                <div className="list-item-icon" style={{ width: 64, height: 64, background: 'var(--color-primary-bg)' }}>
                  <Package size={28} color="var(--color-primary)" />
                </div>
              )}
              <div>
                <h3 style={{ margin: 0 }}>{scanResult.product.name}</h3>
                {scanResult.product.brand && (
                  <p className="text-sm text-secondary" style={{ margin: 0 }}>{scanResult.product.brand}</p>
                )}
              </div>
            </div>

            {/* Quantity selector */}
            <QuantitySelector />

            <div className="form-group">
              <label className="form-label">
                <MapPin size={14} style={{ verticalAlign: 'middle' }} /> {t('scan.selectLocation')}
              </label>
              <select
                className="form-select"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <option value="">{t('scan.selectLocation')}</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              {scanResult.suggested_location && (
                <p className="text-xs text-secondary mt-sm">
                  {t('putAway.suggested', { location: scanResult.suggested_location.name })}
                </p>
              )}
            </div>

            <button
              className="btn btn-primary btn-full btn-lg"
              onClick={handleAddToInventory}
              disabled={loading}
            >
              <Plus size={18} />
              {loading ? t('common.loading') : t('scan.addToInventory')}
            </button>

            <button className="btn btn-ghost btn-full mt-md" onClick={resetScan}>
              {t('scan.cancel')}
            </button>
          </div>
        )}

        {/* === NOT FOUND STATE === */}
        {mode === 'notfound' && scanResult && (
          <div className="card">
            <div className="text-center">
              <Package size={48} color="var(--color-secondary)" style={{ marginBottom: 'var(--space-sm)' }} />
              <h3 className="mb-sm">{t('scan.notFound')}</h3>
              <p className="text-sm text-secondary mb-md">
                {t('scan.notFoundBarcode', { barcode: scanResult.barcode })}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
                <button
                  className="btn btn-primary btn-full"
                  onClick={() => navigate(`/add-item?barcode=${scanResult.barcode}`)}
                >
                  <Plus size={18} /> {t('scan.addManually')}
                </button>
                <button className="btn btn-secondary btn-full" onClick={startCamera}>
                  <Camera size={18} /> {t('scan.scanAnother')}
                </button>
                <button className="btn btn-ghost btn-full" onClick={resetScan}>
                  {t('scan.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* === DUPLICATE STATE === */}
        {mode === 'duplicate' && scanResult && (
          <div className="card">
            <div className="banner banner-info mb-md">
              {t('scan.duplicateMessage', { name: scanResult.product?.name })}
            </div>

            <h3 className="mb-md">{t('scan.duplicate')}</h3>

            {/* Quantity selector for duplicates too */}
            <QuantitySelector />

            {scanResult.existing_items?.map(item => (
              <div key={item.id} className="list-item mb-sm">
                <div className="list-item-content">
                  <div className="list-item-title">{scanResult.product.name}</div>
                  <div className="list-item-subtitle">
                    {item.quantity} {item.unit}
                    {item.location_name && ` — ${item.location_name}`}
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => handleDuplicateAction('increment', item.id)}
                  disabled={loading}
                >
                  <PlusCircle size={16} /> +{quantity}
                </button>
              </div>
            ))}

            <div className="divider" />

            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button
                className="btn btn-primary btn-full"
                onClick={() => handleDuplicateAction('new')}
                disabled={loading}
              >
                <Plus size={18} /> {t('scan.addNew')}
              </button>
              <button className="btn btn-ghost btn-full" onClick={resetScan}>
                {t('scan.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {loading && mode !== 'idle' && (
          <div style={{ textAlign: 'center', padding: 'var(--space-lg)' }}>
            <div className="loader-spinner" style={{ margin: '0 auto' }} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ScanPage;
